// MessageLayer — the frame's messages on screen (pen "Messages" §1): the
// toast stack in the main area, the last-step line beside the bottom row, the
// note at an object, and one polite announcement per new message.
//
// A projection of `frame.messages` (the pure queue, messageQueue.ts) plus its
// intents (`messageControls`, which the shell stamps with the clock). The one
// clock here is `MessageClock`: mounted only while something will run out, it
// ticks through the sanctioned `useNow` seam and reports when the next
// deadline has passed; the queue decides what that means.

export type { MessageControls };
export { MessageLayer };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useEffect, useLayoutEffect, useState } from "react";
import { Button } from "../../components/Button/Button.js";
import {
    LastStepLine,
    MessageMore,
    MessageToast,
    ObjectNote,
    type Rect,
} from "../../components/MessageStack/MessageStack.js";
import { VisuallyHidden } from "../../components/VisuallyHidden/VisuallyHidden.js";
import { useNow } from "../../hooks/useNow.js";
import type { HostNotice } from "../frame.js";
import {
    type Announcement,
    type Burst,
    nextDeadline,
    type QueuedToast,
    type Slot,
    stackView,
} from "../messageQueue.js";
import { useAppShell } from "./context.js";
import { useMessageArea } from "./useMessageArea.js";
import "./messageArea.css";
import { type NoticeViewDeps, useNoticeView } from "./useNoticeView.js";

// The stack's intents; the shell reads the clock for the timed ones.
type MessageControls = {
    readonly tick: () => void;
    readonly pause: () => void;
    readonly resume: () => void;
    readonly dismiss: (id: number) => void;
    readonly expand: () => void;
    readonly collapse: () => void;
};

// How often the clock looks while a deadline is pending.
const CLOCK_MS = 250;

const MessageClock = ({
    deadline,
    onDue,
}: {
    readonly deadline: number;
    readonly onDue: () => void;
}): null => {
    const now = useNow(CLOCK_MS);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `onDue` is a fresh closure each render; only the clock and the deadline decide.
    useEffect(() => {
        if (now >= deadline) onDue();
    }, [now, deadline]);
    return null;
};

const NoticeToast = ({
    toast,
    deps,
    onDismiss,
}: {
    readonly toast: QueuedToast<HostNotice>;
    readonly deps: NoticeViewDeps;
    readonly onDismiss: (id: number) => void;
}): ReactNode => {
    const view = useNoticeView(toast.payload, deps);
    if (view === null) return null;
    return (
        <MessageToast
            tone={view.tone}
            title={view.title}
            description={view.description}
            count={toast.count}
            // Acting on a message is done with it.
            action={
                view.action === undefined ? undefined : (
                    // biome-ignore lint/a11y/noStaticElementInteractions: a click on the action button inside bubbles here; the button is the control.
                    // biome-ignore lint/a11y/useKeyWithClickEvents: the inner button handles the keyboard; this only closes the toast after it.
                    <span onClick={() => onDismiss(toast.id)}>
                        {view.action}
                    </span>
                )
            }
            onClose={() => onDismiss(toast.id)}
        />
    );
};

const BurstToast = ({
    burst,
    onShow,
    onDismiss,
}: {
    readonly burst: Burst;
    readonly onShow: () => void;
    readonly onDismiss: (id: number) => void;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <MessageToast
            tone="neutral"
            title={t`${burst.count} things happened while you worked`}
            description=""
            count={1}
            action={
                <Button variant="secondary" size="sm" onClick={onShow}>
                    {t`Show`}
                </Button>
            }
            onClose={() => onDismiss(burst.id)}
        />
    );
};

const LastStep = ({
    slot,
    deps,
    left,
    bottom,
    controls,
}: {
    readonly slot: Slot<HostNotice>;
    readonly deps: NoticeViewDeps;
    readonly left: number;
    readonly bottom: number;
    readonly controls: MessageControls;
}): ReactNode => {
    const view = useNoticeView(slot.payload, deps);
    return view === null ? null : (
        <LastStepLine
            text={view.title}
            action={
                view.action === undefined ? undefined : (
                    // biome-ignore lint/a11y/noStaticElementInteractions: a click on the action button inside bubbles here; the button is the control.
                    // biome-ignore lint/a11y/useKeyWithClickEvents: the inner button handles the keyboard; this only closes the line after it.
                    <span onClick={() => controls.dismiss(slot.id)}>
                        {view.action}
                    </span>
                )
            }
            left={left}
            bottom={bottom}
            onHold={controls.pause}
            onRelease={controls.resume}
        />
    );
};

// Where the object a note is about is on screen (and the part of it that
// flashes), read from the laid-out canvas. Null when it is not on screen.
type ObjectRects = { readonly object: Rect; readonly flash: Rect };

const rectOf = (el: Element): Rect => {
    const r = el.getBoundingClientRect();
    return { left: r.left, top: r.top, width: r.width, height: r.height };
};

const cardOf = (id: string): Element | null =>
    document.querySelector(`[data-card-id="${CSS.escape(id)}"]`);

const objectRectsOf = (n: HostNotice): ObjectRects | null => {
    switch (n.type) {
        case "already-part": {
            const card = cardOf(n.moduleId);
            if (card === null) return null;
            const part = card.querySelector(
                `[data-part-id="${CSS.escape(n.part)}"]`,
            );
            return { object: rectOf(card), flash: rectOf(part ?? card) };
        }
        case "source-deduped": {
            const card = cardOf(n.sourceId);
            return card === null
                ? null
                : { object: rectOf(card), flash: rectOf(card) };
        }
        default:
            return null;
    }
};

const AtObject = ({
    slot,
    deps,
}: {
    readonly slot: Slot<HostNotice>;
    readonly deps: NoticeViewDeps;
}): ReactNode => {
    const view = useNoticeView(slot.payload, deps);
    const [rects, setRects] = useState<ObjectRects | null>(null);
    // Follows the object while the note shows: the canvas may still be
    // bringing it into view, or be panned (view-ephemeral layout reads).
    useLayoutEffect(() => {
        let frame = 0;
        const follow = () => {
            const next = objectRectsOf(slot.payload);
            setRects((prev) =>
                JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
            );
            frame = requestAnimationFrame(follow);
        };
        follow();
        return () => cancelAnimationFrame(frame);
    }, [slot]);
    return view === null || rects === null ? null : (
        <ObjectNote
            object={rects.object}
            flash={rects.flash}
            label={view.title}
        />
    );
};

const AnnounceToast = ({
    payload,
    deps,
}: {
    readonly payload: HostNotice;
    readonly deps: NoticeViewDeps;
}): string => {
    const view = useNoticeView(payload, deps);
    return view === null
        ? ""
        : [view.title, view.description].filter((s) => s !== "").join(". ");
};

const Announce = ({
    announcement,
    deps,
}: {
    readonly announcement: Announcement<HostNotice>;
    readonly deps: NoticeViewDeps;
}): ReactNode => {
    const { t } = useLingui();
    switch (announcement.type) {
        case "toast":
            return <AnnounceToast payload={announcement.payload} deps={deps} />;
        case "burst":
            return t`${announcement.count} things happened while you worked`;
        default:
            return exhaustSwitchCase(announcement);
    }
};

const MessageLayer = (): ReactNode => {
    const { t } = useLingui();
    const { frame, tabs, messageControls: c, noticeDeps } = useAppShell();
    const q = frame.messages;
    const stack = stackView(q);
    const area = useMessageArea(
        `${tabs.active.type}:${stack.toasts.length}:${q.lastStep?.id ?? 0}`,
    );
    const deadline = nextDeadline(q);
    const hasStack = stack.toasts.length > 0 || stack.burst !== null;
    return (
        <>
            {deadline === null ? null : (
                <MessageClock deadline={deadline} onDue={c.tick} />
            )}
            {area === null || !hasStack ? null : (
                <div
                    className="bilda-message-area"
                    style={{
                        left: area.left,
                        right: area.right,
                        bottom: area.bottom,
                    }}
                >
                    <section
                        className="bilda-messages"
                        aria-label={t`Notifications`}
                        onMouseEnter={c.pause}
                        onMouseLeave={c.resume}
                        onFocus={c.pause}
                        onBlur={c.resume}
                    >
                        {stack.more > 0 ? (
                            <MessageMore
                                label={t`+${stack.more} more`}
                                onClick={c.expand}
                            />
                        ) : q.expanded ? (
                            <MessageMore
                                label={t`Show fewer`}
                                onClick={c.collapse}
                            />
                        ) : null}
                        {stack.toasts.map((toast) => (
                            <NoticeToast
                                key={toast.id}
                                toast={toast}
                                deps={noticeDeps}
                                onDismiss={c.dismiss}
                            />
                        ))}
                        {stack.burst === null ? null : (
                            <BurstToast
                                key={stack.burst.id}
                                burst={stack.burst}
                                onShow={c.expand}
                                onDismiss={c.dismiss}
                            />
                        )}
                    </section>
                </div>
            )}
            {area === null || q.lastStep === null ? null : (
                <LastStep
                    key={q.lastStep.id}
                    slot={q.lastStep}
                    deps={noticeDeps}
                    left={area.lineLeft}
                    bottom={area.lineBottom}
                    controls={c}
                />
            )}
            {q.atObject === null ? null : (
                <AtObject
                    key={q.atObject.id}
                    slot={q.atObject}
                    deps={noticeDeps}
                />
            )}
            <VisuallyHidden as="div" role="status" aria-live="polite">
                {q.announcement === null ? null : (
                    <Announce announcement={q.announcement} deps={noticeDeps} />
                )}
            </VisuallyHidden>
        </>
    );
};
